import * as React from 'react';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';

interface TruncationTooltipProps {
  /** From useIsTruncated — gates whether hovering can open the tooltip. */
  isTruncated: boolean;
  /** The full, untruncated content shown in the tooltip. */
  content: React.ReactNode;
  side?: React.ComponentProps<typeof TooltipContent>['side'];
  /** Passed to TooltipContent — e.g. a max-width + whitespace-normal cap. */
  contentClassName?: string;
  /** The (ref-measured) element the tooltip attaches to. */
  children: React.ReactNode;
}

/**
 * "Show the full text on hover, but only when it's actually cut off."
 *
 * The Tooltip/Trigger tree stays MOUNTED no matter what `isTruncated` is —
 * only the `open` gate flips. The tempting alternative,
 * `isTruncated ? <Tooltip>…{el}…</Tooltip> : el`, changes the element type at
 * that tree position, so React unmounts and remounts the measured node when
 * truncation first flips; useIsTruncated's ResizeObserver (keyed on content,
 * not on the node) keeps watching the detached original and every later
 * resize is silently missed. Same constant-tree pattern as
 * PlatformCreatorCell in lib/platformProfile.ts.
 */
export function TruncationTooltip({
  isTruncated,
  content,
  side = 'top',
  contentClassName,
  children,
}: TruncationTooltipProps) {
  const [open, setOpen] = React.useState(false);
  return (
    <Tooltip open={isTruncated && open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      {isTruncated ? (
        <TooltipContent side={side} className={contentClassName}>
          {content}
        </TooltipContent>
      ) : null}
    </Tooltip>
  );
}
